import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DynamicWidgetQueryService } from '@/services/dynamicWidgetQueryService';
import { useTenant } from '@/contexts/TenantContext';
import { scopeReachKey } from '@/lib/mergedScope';
import type { DynamicWidgetConfig, QueryResult } from '@/types/dashboard';

/**
 * Options for useDynamicWidgetData hook
 */
interface UseDynamicWidgetDataOptions {
  enabled?: boolean;
  refetchInterval?: number;
  staleTime?: number;
  cacheTime?: number;
  isPreview?: boolean; // Flag to differentiate preview queries from actual widget queries
}

/**
 * React Query hook for fetching dynamic widget data (DASH-8.1)
 * Integrates with DynamicWidgetQueryService and provides caching
 *
 * DASH-26.0: Added config validation to prevent crashes when config is incomplete
 * (e.g., during drawer close when state is being reset)
 */
export function useDynamicWidgetData(
  config: DynamicWidgetConfig,
  options: UseDynamicWidgetDataOptions = {}
) {
  // DASH-26.0: Validate config has required properties before building query key
  // This prevents crashes when config is incomplete (e.g., during drawer close)
  const isValidConfig = Boolean(
    config?.dataSource &&
    config?.aggregation?.type &&
    config?.visualization?.type
  );

  /**
   * SUB-5.12 · AC7. ⚠️ THE CACHE KEY HAS TO CARRY THE SCOPE, because the same
   * config now resolves to a different set of rows in a different scope.
   *
   * `scopeReachKey` is deliberately narrower than `mergedScopeKey`: it moves
   * only when the accounts in reach move, so an Agency Admin merely being
   * OFFERED All Accounts does not drop every widget's cache. Its own header has
   * the argument.
   *
   * ⚠️ `unresolved` IS ITS OWN VALUE and is never folded into single scope. An
   * entry written before the reach was known must not be served after it is.
   */
  const { mergedScope } = useTenant();
  const scopeKey = scopeReachKey(mergedScope);

  // Build query key using JSON serialization for reliable cache invalidation
  // This ensures that ANY change to the config will trigger a refetch
  // Previously: Individual properties were added to queryKey, but complex nested objects
  // weren't properly compared, causing stale data after widget updates
  // NOTE: Preview queries use 'preview' discriminator to prevent cache collision with actual widgets
  // DASH-26.0: Use placeholder key when config is invalid to prevent accessing undefined properties
  const queryKey = isValidConfig ? [
    'dynamic-widget',
    options.isPreview ? 'preview' : 'widget', // Separate preview queries from actual widget queries
    config.dataSource,
    // SUB-5.12 · AC7. The accounts this entry was read across.
    scopeKey,
    // Serialize filters to ensure nested object changes are detected
    JSON.stringify(config.filters || {}),
    // Serialize aggregation config
    JSON.stringify(config.aggregation),
    // Serialize visualization config (excluding display-only properties)
    JSON.stringify({
      type: config.visualization.type,
      groupBy: config.visualization.groupBy,
      sortBy: config.visualization.sortBy,
      sortOrder: config.visualization.sortOrder,
      showTrend: config.visualization.showTrend,
    }),
  ] : ['dynamic-widget', 'invalid-config'];

  return useQuery<QueryResult, Error>({
    queryKey,
    queryFn: async () => {
      // DASH-26.0: Runtime validation (should never hit due to enabled check, but defensive)
      if (!isValidConfig) {
        throw new Error('Invalid widget configuration: missing required fields');
      }

      const startTime = performance.now();

      const result = await DynamicWidgetQueryService.executeQuery(config);

      const endTime = performance.now();
      const duration = endTime - startTime;

      // Log slow queries (> 2s)
      if (duration > 2000) {
        console.warn(
          `[SLOW QUERY] DynamicWidget: ${duration.toFixed(0)}ms`,
          {
            dataSource: config.dataSource,
            filters: config.filters,
            aggregation: config.aggregation.type,
          }
        );
      }

      return result;
    },
    staleTime: options.staleTime ?? 20000, // 20 seconds
    gcTime: options.cacheTime ?? 300000, // 5 minutes (gcTime replaces cacheTime in newer react-query)
    refetchInterval: options.refetchInterval ?? (isValidConfig && config.display?.refreshInterval || 30000),
    // DASH-26.0: Disable query when config is invalid to prevent crashes
    enabled: isValidConfig && (options.enabled ?? true),
    retry: 1, // Retry once on failure
    retryDelay: 1000, // Wait 1s before retry
  });
}

/**
 * Hook for fetching filter options dynamically from tenant data
 */
export function useFilterOptions(
  filterType: string,
  params?: {
    pipelineIds?: number[];
  }
) {
  return useQuery({
    queryKey: ['filter-options', filterType, params?.pipelineIds],
    queryFn: () => DynamicWidgetQueryService.getFilterOptions(filterType, params),
    staleTime: 300000, // 5 minutes
    gcTime: 600000, // 10 minutes
    refetchOnMount: false, // Use cache if available
    retry: 1,
  });
}

/**
 * Hook for prefetching filter options (performance optimization)
 * Use when FilterBuilderDrawer opens to load options proactively
 */
export function usePrefetchFilterOptions() {
  const queryClient = useQueryClient();

  return {
    prefetchPipelines: () => {
      queryClient.prefetchQuery({
        queryKey: ['filter-options', 'pipelines'],
        queryFn: () => DynamicWidgetQueryService.getFilterOptions('pipelines'),
        staleTime: 300000,
      });
    },
    prefetchStages: (pipelineIds?: number[]) => {
      queryClient.prefetchQuery({
        queryKey: ['filter-options', 'stages', pipelineIds],
        queryFn: () => DynamicWidgetQueryService.getFilterOptions('stages', { pipelineIds }),
        staleTime: 300000,
      });
    },
    prefetchUsers: () => {
      queryClient.prefetchQuery({
        queryKey: ['filter-options', 'users'],
        queryFn: () => DynamicWidgetQueryService.getFilterOptions('users'),
        staleTime: 300000,
      });
    },
  };
}
